import { useEffect, useMemo, useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Input } from '../../components/atoms/Input'
import { Modal } from '../../components/atoms/Modal'
import { Textarea } from '../../components/atoms/Textarea'
import { Field } from '../../components/molecules/Field'
import type {
  CreateOrganisationInput,
  OrganisationAddressInput,
  OrganisationDto,
  UpdateOrganisationInput
} from '../../../viewmodels/useOrganisations'

const NAME_MIN = 1
const NAME_MAX = 128
const URL_MAX = 256
const DESCRIPTION_MAX = 2048
const LABEL_MAX = 64
const LINE_MAX = 256
const CITY_MAX = 128
const STATE_MAX = 128
const PIN_MAX = 16
const COUNTRY_MAX = 64
// URLs must use https:// (Kiran 2026-06-08). Plain http:// is rejected
// — the brand is HTTPS-only end-to-end (Cloudflare-managed certs on
// every tenant subdomain), so any http:// link is a typo / paste error
// rather than something we want to honour.
const URL_PATTERN = /^https:\/\/\S+$/i

interface Props {
  readonly open: boolean
  readonly mode: 'create' | 'edit'
  readonly initial: OrganisationDto | null
  readonly onClose: () => void
  readonly onSubmit: (
    input: CreateOrganisationInput | UpdateOrganisationInput
  ) => Promise<{ ok: true } | { ok: false; field?: string | null; message: string }>
}

interface AddressDraft {
  /** Local-only row key; not sent to the server. */
  readonly draftId: string
  label: string
  line1: string
  line2: string
  city: string
  state: string
  pin: string
  country: string
}

interface FormState {
  name: string
  company_url: string
  description: string
  addresses: AddressDraft[]
}

const EMPTY: FormState = {
  name: '',
  company_url: '',
  description: '',
  addresses: []
}

function freshDraftId(): string {
  return `draft-${Math.random().toString(36).slice(2)}`
}

function emptyAddress(): AddressDraft {
  return {
    draftId: freshDraftId(),
    label: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    pin: '',
    country: 'India'
  }
}

function clientValidate(state: FormState): { field: string; message: string } | null {
  const name = state.name.trim()
  if (name.length < NAME_MIN || name.length > NAME_MAX) {
    return { field: 'name', message: `name must be ${NAME_MIN}..${NAME_MAX} chars` }
  }
  const url = state.company_url.trim()
  if (url !== '') {
    if (url.length > URL_MAX) return { field: 'company_url', message: `company_url must be <=${URL_MAX} chars` }
    if (!URL_PATTERN.test(url)) return { field: 'company_url', message: 'company_url must start with https://' }
  }
  if (state.description.length > DESCRIPTION_MAX) {
    return { field: 'description', message: `description must be <=${DESCRIPTION_MAX} chars` }
  }
  for (let i = 0; i < state.addresses.length; i++) {
    const a = state.addresses[i]!
    const reqs: Array<[keyof AddressDraft, string, number]> = [
      ['label', `addresses[${i}].label`, LABEL_MAX],
      ['line1', `addresses[${i}].line1`, LINE_MAX],
      ['city',  `addresses[${i}].city`,  CITY_MAX],
      ['state', `addresses[${i}].state`, STATE_MAX],
      ['pin',   `addresses[${i}].pin`,   PIN_MAX],
      ['country', `addresses[${i}].country`, COUNTRY_MAX]
    ]
    for (const [key, label, max] of reqs) {
      const v = (a[key] as string).trim()
      if (v.length < 1 || v.length > max) {
        return { field: label, message: `${label} must be 1..${max} chars` }
      }
    }
    if (a.line2.length > LINE_MAX) {
      return { field: `addresses[${i}].line2`, message: `addresses[${i}].line2 must be <=${LINE_MAX} chars` }
    }
  }
  return null
}

/**
 * OrganisationFormModal — Add + Edit shared form. Header fields
 * (name / company URL / description) plus a nested editable list of
 * addresses (label + line1 + line2 + city + state + pin + country).
 *
 * The address list is REPLACED on PATCH (full sync — same shape as
 * other sync flows in the codebase). Operator can add / remove address
 * rows freely; their order on screen is preserved as sort_order on the
 * wire.
 */
export function OrganisationFormModal({
  open,
  mode,
  initial,
  onClose,
  onSubmit
}: Props): JSX.Element {
  const [state, setState] = useState<FormState>(EMPTY)
  const [submitting, setSubmitting] = useState(false)
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  // Master org (seeded Uyyamadilu): name / URL / description are
  // locked at the repo + the UI. Only addresses editable (Kiran
  // 2026-06-08).
  const isMaster = initial !== null && initial.is_master

  useEffect(() => {
    if (open) {
      setState(
        initial === null
          ? { ...EMPTY, addresses: [emptyAddress()] }   // one blank row to start
          : {
              name: initial.name,
              company_url: initial.company_url ?? '',
              description: initial.description ?? '',
              addresses: initial.addresses.map((a) => ({
                draftId: freshDraftId(),
                label: a.label,
                line1: a.line1,
                line2: a.line2 ?? '',
                city: a.city,
                state: a.state,
                pin: a.pin,
                country: a.country
              }))
            }
      )
      setFieldError(null)
      setFormError(null)
      setSubmitting(false)
    }
  }, [open, initial])

  const handleSubmit = async (): Promise<void> => {
    const clientErr = clientValidate(state)
    if (clientErr !== null) {
      setFieldError(clientErr)
      return
    }
    setFieldError(null)
    setFormError(null)
    setSubmitting(true)
    const addresses: OrganisationAddressInput[] = state.addresses.map((a, i) => ({
      label: a.label.trim(),
      sort_order: i,
      line1: a.line1.trim(),
      line2: a.line2.trim() === '' ? null : a.line2.trim(),
      city: a.city.trim(),
      state: a.state.trim(),
      pin: a.pin.trim(),
      country: a.country.trim()
    }))
    // Master-org edit: send only `addresses` (the one editable field).
    // The backend guard would reject any echo of name/company_url/
    // description anyway, but stripping them client-side also keeps the
    // PATCH payload aligned with the "only addresses are editable"
    // intent and avoids accidental drift if a future field is added.
    const payload: CreateOrganisationInput | UpdateOrganisationInput = isMaster
      ? { addresses }
      : {
          name: state.name.trim(),
          company_url: state.company_url.trim() === '' ? null : state.company_url.trim(),
          description: state.description.trim() === '' ? null : state.description.trim(),
          addresses
        }
    const result = await onSubmit(payload)
    setSubmitting(false)
    if (result.ok) {
      onClose()
      return
    }
    if (result.field !== null && result.field !== undefined) {
      setFieldError({ field: result.field, message: result.message })
    } else {
      setFormError(result.message)
    }
  }

  const headerFieldError = (f: keyof FormState): string | null =>
    fieldError !== null && fieldError.field === f ? fieldError.message : null

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'create' ? 'Add organisation' : `Edit ${initial?.name ?? ''}`}
      maxWidth="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting}>
            {submitting ? 'Saving…' : mode === 'create' ? 'Add' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError !== null ? <Alert tone="error">{formError}</Alert> : null}
        {isMaster ? (
          <Alert tone="info">
            Master organisation — only the address set is editable.
            Name, URL, and description are locked.
          </Alert>
        ) : null}

        <Field label="Name" error={headerFieldError('name')}>
          <Input
            value={state.name}
            hasError={headerFieldError('name') !== null}
            onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
            placeholder="e.g. Acme Bamboo Co."
            maxLength={NAME_MAX}
            disabled={isMaster}
          />
        </Field>

        <Field
          label="Company URL"
          helper={isMaster ? 'Locked — matches the apex domain.' : 'Optional. Full URL including https://'}
          error={headerFieldError('company_url')}
        >
          <Input
            value={state.company_url}
            hasError={headerFieldError('company_url') !== null}
            onChange={(e) => setState((s) => ({ ...s, company_url: e.target.value }))}
            placeholder="https://www.acmebamboo.com"
            maxLength={URL_MAX}
            disabled={isMaster}
          />
        </Field>

        <Field label="Description" error={headerFieldError('description')}>
          <Textarea
            value={state.description}
            rows={3}
            hasError={headerFieldError('description') !== null}
            onChange={(e) => setState((s) => ({ ...s, description: e.target.value }))}
            placeholder="Optional notes about this organisation"
            maxLength={DESCRIPTION_MAX}
            disabled={isMaster}
          />
        </Field>

        <AddressList
          addresses={state.addresses}
          fieldError={fieldError}
          onChange={(addresses) => setState((s) => ({ ...s, addresses }))}
        />
      </div>
    </Modal>
  )
}

function AddressList({
  addresses,
  fieldError,
  onChange
}: {
  readonly addresses: AddressDraft[]
  readonly fieldError: { field: string; message: string } | null
  readonly onChange: (next: AddressDraft[]) => void
}): JSX.Element {
  const errorIndex = useMemo<number | null>(() => {
    if (fieldError === null) return null
    const m = fieldError.field.match(/^addresses\[(\d+)\]/)
    return m === null ? null : Number(m[1])
  }, [fieldError])

  const rowFieldError = (i: number, key: keyof AddressDraft): string | null => {
    if (errorIndex !== i || fieldError === null) return null
    return fieldError.field === `addresses[${i}].${key}` ? fieldError.message : null
  }

  const setRow = (i: number, patch: Partial<AddressDraft>): void => {
    onChange(addresses.map((a, idx) => (idx === i ? { ...a, ...patch } : a)))
  }
  const addRow = (): void => {
    onChange([...addresses, emptyAddress()])
  }
  const removeRow = (i: number): void => {
    onChange(addresses.filter((_, idx) => idx !== i))
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="font-body text-body-sm font-medium text-charcoal">Addresses</p>
        <Button size="sm" variant="secondary" onClick={addRow}>
          Add address
        </Button>
      </div>
      {addresses.length === 0 ? (
        <p className="font-body text-body-sm text-text-secondary">No addresses yet.</p>
      ) : null}
      <div className="space-y-4">
        {addresses.map((a, i) => (
          <div key={a.draftId} className="border border-border rounded-md bg-parchment/40 p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <Field label="Label" error={rowFieldError(i, 'label')}>
                <Input
                  value={a.label}
                  hasError={rowFieldError(i, 'label') !== null}
                  onChange={(e) => setRow(i, { label: e.target.value })}
                  placeholder="e.g. HQ, Branch — Bangalore"
                  maxLength={LABEL_MAX}
                />
              </Field>
              <Button size="sm" variant="ghost" onClick={() => removeRow(i)}>
                Remove
              </Button>
            </div>
            <Field label="Line 1" error={rowFieldError(i, 'line1')}>
              <Input
                value={a.line1}
                hasError={rowFieldError(i, 'line1') !== null}
                onChange={(e) => setRow(i, { line1: e.target.value })}
                placeholder="Street / building"
                maxLength={LINE_MAX}
              />
            </Field>
            <Field label="Line 2 (optional)" error={rowFieldError(i, 'line2')}>
              <Input
                value={a.line2}
                hasError={rowFieldError(i, 'line2') !== null}
                onChange={(e) => setRow(i, { line2: e.target.value })}
                placeholder="Apartment / suite / area"
                maxLength={LINE_MAX}
              />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="City" error={rowFieldError(i, 'city')}>
                <Input
                  value={a.city}
                  hasError={rowFieldError(i, 'city') !== null}
                  onChange={(e) => setRow(i, { city: e.target.value })}
                  maxLength={CITY_MAX}
                />
              </Field>
              <Field label="State" error={rowFieldError(i, 'state')}>
                <Input
                  value={a.state}
                  hasError={rowFieldError(i, 'state') !== null}
                  onChange={(e) => setRow(i, { state: e.target.value })}
                  maxLength={STATE_MAX}
                />
              </Field>
              <Field label="PIN" error={rowFieldError(i, 'pin')}>
                <Input
                  value={a.pin}
                  hasError={rowFieldError(i, 'pin') !== null}
                  onChange={(e) => setRow(i, { pin: e.target.value })}
                  maxLength={PIN_MAX}
                />
              </Field>
              <Field label="Country" error={rowFieldError(i, 'country')}>
                <Input
                  value={a.country}
                  hasError={rowFieldError(i, 'country') !== null}
                  onChange={(e) => setRow(i, { country: e.target.value })}
                  maxLength={COUNTRY_MAX}
                />
              </Field>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
